import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    useMemo,
    useState
} from "react";

import { capitalize } from "lodash";
import { isDesktop } from "react-device-detect";

type PossibleDuplicateSelectorProps<TItem> = {
    readonly proposedNewItem: React.ReactNode;
    readonly renderPossibleDuplicateItem: (item: TItem, isCurrentSelection: boolean, onClick: () => void, key: string) => React.ReactNode;
    readonly possibleDuplicates: Array<TItem>;
    readonly onUseExisting: (existingItem: TItem) => void | Promise<void>;
    readonly twoColumnDuplicateLayout?: boolean;
    readonly itemTypeDisplayString: string;
    readonly pluralItemTypeDisplayString?: string;
};

export const PossibleDuplicateSelector = <TItem,>(props: PossibleDuplicateSelectorProps<TItem>) => {
    const { proposedNewItem, renderPossibleDuplicateItem, possibleDuplicates, onUseExisting, itemTypeDisplayString, twoColumnDuplicateLayout = false, pluralItemTypeDisplayString } = props;
    const [selectedItem, setSelectedItem] = useState<number>(-1);
    const theme = useTheme();
    const isScreenSizeSmallerThanMD = useMediaQuery(theme.breakpoints.down("md"));
    const isPortrait = useMediaQuery("(orientation: portrait)");

    const isOneColumnLayout = useMemo(() => {
        if (isDesktop) {
            return isScreenSizeSmallerThanMD;
        }
        return isPortrait;
    }, [isDesktop, isPortrait, isScreenSizeSmallerThanMD]);

    return (
        <Box sx={{ width: "100%" }}>
            <Typography margin={0.5}>
                {`The ${itemTypeDisplayString} you are trying to create has similar entries in our system, would you like to use one of them instead?`}
            </Typography>
            <Typography margin={0.5}>
                {`If you wish to use an existing ${itemTypeDisplayString}, please select the ${itemTypeDisplayString} and
                hit next. To use the new one you are creating, please hit “Continue with new ${itemTypeDisplayString}`}
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `${isOneColumnLayout ? "1fr" : "repeat(2, 1fr)"}`,
                    gap: "16px",
                }}
            >
                <Box sx={{ width: "100%" }}>
                    <Card>
                        <CardContent sx={{ "&:last-child": { paddingBottom: 0 } }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    marginLeft: "16px",
                                    marginBottom: "16px",
                                    fontWeight: "bold",
                                }}
                            >
                                {`New ${capitalize(itemTypeDisplayString)}`}
                            </Typography>
                            {proposedNewItem}
                        </CardContent>
                    </Card>
                </Box>
                <Card
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        height: "800px",
                    }}
                >
                    <CardContent
                        sx={{
                            flexGrow: 1,
                            overflowY: "auto", // Enables vertical scrolling
                            maxHeight: 800, // Limit the height to create a scrollable area
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                marginLeft: "16px",
                                marginBottom: "16px",
                                fontWeight: "bold",
                            }}
                        >
                            {"Existing " + capitalize((pluralItemTypeDisplayString !== undefined ? pluralItemTypeDisplayString : `${itemTypeDisplayString}s`))}
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: twoColumnDuplicateLayout && !isOneColumnLayout ? "repeat(2, 1fr)" : "2fr",
                                width: "100%",
                                gap: (theme) => theme.spacing(3),
                            }}
                        >
                            {possibleDuplicates.map((item, id) => renderPossibleDuplicateItem(item, selectedItem === id, () => setSelectedItem(id), `duplicate-${id}`))}
                        </Box>
                    </CardContent>
                    <CardActions
                        sx={{
                            width: "100%",
                            position: "sticky",
                            bottom: 0,
                            backgroundColor: "background.paper", // Keeps the buttons visible over scrolling content
                            zIndex: 1, // Ensures that the actions area stays on top of scrolling content
                            justifyContent: "flex-end",
                        }}
                    >
                        {possibleDuplicates && (
                            <Button
                                variant="contained"
                                disabled={selectedItem < 0}
                                onClick={() => onUseExisting(possibleDuplicates[selectedItem])}
                            >
                                {`Use Existing ${capitalize(itemTypeDisplayString)}`}
                            </Button>
                        )}
                    </CardActions>
                </Card>
            </Box>
        </Box >
    );
};
